import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import type { ReactNode } from 'react'

import { MetaText } from '@/components/atoms/MetaText'
import type { YakurekiDetail } from '@/shared/types/yakurekiCore'

import type {
  GuidanceAudit,
  GuidanceSupportDataV3,
  PatientAttributesResponse,
} from '../../types'
import { formatMonthDayTime } from '../../utils/formatPrepDate'
import { AuditHitList } from '../AuditHitList'
import { PatientAttributeTable } from '../PatientAttributeTable'
import { EmptyLine, PrepSection, SectionDivider } from './parts'
import { PreviousDiffSection } from './PreviousDiffSection'

// 申し送りの上限. 遠隔服薬指導の handover_note と同じ (yakureki-back log/262 work-log/08 §6.1).
export const HANDOVER_NOTE_MAX_LENGTH = 500

interface Props {
  event: YakurekiDetail
  // 指導前準備 (completed のときだけ). それ以外は pending を出す
  data: GuidanceSupportDataV3 | null
  pending: ReactNode
  attributes: {
    data: PatientAttributesResponse | undefined
    isLoading: boolean
    isError: boolean
  }
  audit: GuidanceAudit | null
  draft: string
  onDraftChange: (value: string) => void
  onFinish: () => void
  isFinishing: boolean
  isFinishError: boolean
}

const AttributesBody = ({
  attributes,
}: {
  attributes: Props['attributes']
}) => {
  if (attributes.isLoading) return <MetaText>読み込み中…</MetaText>
  if (attributes.isError || !attributes.data) {
    return <EmptyLine>(取得できませんでした)</EmptyLine>
  }
  if (attributes.data.status === 'patient_not_linked') {
    return <EmptyLine>(患者につながっていない来局です)</EmptyLine>
  }
  return (
    <PatientAttributeTable
      attributes={attributes.data.attributes}
      patient={attributes.data.patient}
    />
  )
}

// 監査の面 (選んだときだけ開く). 上から
//   1. 監査の要点 (今日の薬 × 患者の属性)
//   2. 患者の属性 (7 行)
//   3. 前回との差
//   4. 申し送り + 「監査を終える」
// (yakureki-back log/262 work-log/08 §6.2. 見出しの語は決定 15)
export const AuditFace = ({
  event,
  data,
  pending,
  attributes,
  audit,
  draft,
  onDraftChange,
  onFinish,
  isFinishing,
  isFinishError,
}: Props) => {
  const contraindicatedPoints = (data?.checkPoints ?? []).filter(
    (p) => p.is_contraindicated
  )
  const auditedAt = audit ? formatMonthDayTime(audit.auditedAt) : null
  return (
    <Stack spacing={2}>
      <PrepSection title="監査の要点">
        {data ? (
          <AuditHitList
            hits={data.auditHits ?? null}
            contraindicatedPoints={contraindicatedPoints}
          />
        ) : (
          pending
        )}
      </PrepSection>
      <SectionDivider />

      {/* 見出しの脇に抽出の日付を置かない. 各行の出所が日付を持つ (hub design-book ADR-0061) */}
      <PrepSection title="患者の属性">
        <AttributesBody attributes={attributes} />
      </PrepSection>
      <SectionDivider />

      <PreviousDiffSection event={event} isInitial={!!data?.isInitial} />
      <SectionDivider />

      <PrepSection title="申し送り">
        <Stack spacing={1}>
          <TextField
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            placeholder="気づいたことを書く (なければ空のまま)"
            multiline
            minRows={2}
            size="small"
            fullWidth
            inputProps={{
              maxLength: HANDOVER_NOTE_MAX_LENGTH,
              'aria-label': '申し送り',
            }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1.5,
            }}
          >
            {isFinishError ? (
              <Typography variant="caption" sx={{ color: 'error.main' }}>
                送れませんでした
              </Typography>
            ) : (
              audit && (
                <MetaText>
                  {['監査済み', auditedAt, audit.auditedBy]
                    .filter(Boolean)
                    .join(' ')}
                </MetaText>
              )
            )}
            <Button
              variant="contained"
              size="small"
              disableElevation
              disabled={isFinishing}
              onClick={onFinish}
            >
              監査を終える
            </Button>
          </Box>
        </Stack>
      </PrepSection>
    </Stack>
  )
}
